import type { Inbox } from '../api/types';
import { channelKeyForInbox, inboxCanReach, type ChannelKey } from './commsMeta';

/**
 * Which channel a new message goes out on, when nobody has said.
 *
 * The compose window asks its two questions in the order a mail client does —
 * the channel first, addressed from the tab the agent was standing on, then the
 * person. That order means the panel can be pointed at Email and handed a phone
 * number, so something has to decide which of the two yields. It is always the
 * channel: a recipient is a fact the agent typed, and the channel was a default
 * nobody chose.
 *
 * Pure and inbox-list-driven so the rule below can be pinned by a test rather
 * than discovered on a Monday morning.
 */

/** What `inboxCanReach` needs to know about a recipient, whoever they are. */
export interface ReachShape {
  email: string | null;
  phoneNumber: string | null;
}

/**
 * Which channel to CHOOSE when several could reach somebody and nobody said.
 *
 * SMS before WhatsApp, which is NOT the order they are drawn in. A first
 * outbound WhatsApp message has no session window to sit inside and needs an
 * approved template, which the compose window has no picker for — so picking it
 * for an agent would send, fail, and explain a 24-hour rule they never opened.
 * An SMS to the same number simply goes. Telegram is last and never reachable
 * anyway (a bot cannot open a chat), so it only ever appears greyed.
 */
export const FALLBACK_ORDER: ChannelKey[] = ['email', 'sms', 'whatsapp', 'telegram'];

/** Every inbox on that channel which can reach this recipient, in list order. */
export function inboxesReaching(
  inboxes: Inbox[],
  key: ChannelKey,
  shape: ReachShape,
): Inbox[] {
  return inboxes.filter(
    (inbox) => channelKeyForInbox(inbox) === key && inboxCanReach(inbox, shape),
  );
}

/**
 * The channel and account to send on. `preferred` — the tab the panel opened
 * on, or a channel the agent just pressed — wins whenever it can reach them;
 * otherwise the first of FALLBACK_ORDER that can. Null when nothing can, which
 * is a real answer the panel shows rather than a failure.
 */
export function channelReaching(
  inboxes: Inbox[],
  shape: ReachShape,
  preferred?: ChannelKey | null,
): { key: ChannelKey; inbox: Inbox } | null {
  if (preferred) {
    const kept = inboxesReaching(inboxes, preferred, shape);
    if (kept.length) return { key: preferred, inbox: kept[0] };
  }
  for (const key of FALLBACK_ORDER) {
    const candidates = inboxesReaching(inboxes, key, shape);
    if (candidates.length) return { key, inbox: candidates[0] };
  }
  return null;
}
